import { useState } from "react";
import {
  FormField,
  PasswordInput,
  SubmitButton,
  SubmittingHint,
  useSubmittingMutation,
} from "@/design-system/components/ui";
import { portalAuthApi } from "../api/auth";
import { PortalApiError } from "../api/client";

// Admin/staff-initiated password reset — the shared "change your own password"
// form. Used by the forced change-password gate (PortalForcedPasswordChange,
// shown when a staff member assigned a temporary password) and the Account &
// settings tab. Posts the current password (the temp password in the forced
// case) + the chosen new one to POST /auth/change-password, which clears the
// MustChangePassword gate server-side.
interface Props {
  /** Called after the password is successfully changed (e.g. auth.refresh()). */
  onChanged?: () => void;
  /** Label for the primary button. Defaults to "Update password". */
  submitLabel?: string;
  /** Helper copy under the current-password field (context varies by caller). */
  currentPasswordHelper?: string;
}

export function PortalChangePasswordForm({
  onChanged,
  submitLabel = "Update password",
  currentPasswordHelper,
}: Props) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { submit, pending } = useSubmittingMutation(
    () => portalAuthApi.changePassword(current, next),
    {
      onSuccess: () => {
        setDone(true);
        setCurrent("");
        setNext("");
        setConfirm("");
        onChanged?.();
      },
      onError: (err) => {
        const msg =
          err instanceof PortalApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Couldn't change your password. Try again.";
        setError(msg);
      },
    },
  );

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setDone(false);
    if (!current) {
      setError("Enter your current password.");
      return;
    }
    if (next.length < 12) {
      setError("New password must be at least 12 characters.");
      return;
    }
    if (next !== confirm) {
      setError("New passwords don't match.");
      return;
    }
    void submit();
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-space-4" noValidate>
      <FormField label="Current password" required helperText={currentPasswordHelper}>
        <PasswordInput
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          disabled={pending}
          autoComplete="current-password"
        />
      </FormField>
      <FormField label="New password" required helperText="At least 12 characters.">
        <PasswordInput
          value={next}
          onChange={(e) => setNext(e.target.value)}
          disabled={pending}
          autoComplete="new-password"
        />
      </FormField>
      <FormField label="Confirm new password" required>
        <PasswordInput
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          disabled={pending}
          autoComplete="new-password"
        />
      </FormField>
      {error && (
        <p className="text-small text-status-error m-0" role="alert">
          {error}
        </p>
      )}
      {done && (
        <p className="text-small text-status-success m-0" role="status">
          Password updated.
        </p>
      )}
      <SubmitButton type="submit" pending={pending} pendingLabel="Saving…">
        {submitLabel}
      </SubmitButton>
      <SubmittingHint pending={pending} />
    </form>
  );
}
